/**
 * Super Admin Page
 *
 * Provides organization management for Super Admins (users with administrator=true).
 * Features:
 * - List all organizations with owner info
 * - Create new organizations with owner assignment
 */

import { useState, useEffect, useCallback } from "react";
import { useNotify } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Plus, RefreshCw, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "../providers/supabase/supabase";

interface Organization {
    id: number;
    name: string;
    slug: string;
    isActive: boolean;
    billingStatus: string;
    billingPlan: string;
    maxUsers: number;
    maxContacts: number;
    createdAt: string;
    owner: {
        id: number;
        email: string;
        firstName: string;
        lastName: string;
    } | null;
}

interface CreateOrgFormData {
    orgName: string;
    orgSlug: string;
    ownerEmail: string;
    ownerFirstName: string;
    ownerLastName: string;
    billingPlan: string;
}

const initialFormData: CreateOrgFormData = {
    orgName: "",
    orgSlug: "",
    ownerEmail: "",
    ownerFirstName: "",
    ownerLastName: "",
    billingPlan: "starter",
};

export const SuperAdminPage = () => {
    const notify = useNotify();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<CreateOrgFormData>(initialFormData);

    const fetchOrganizations = useCallback(async () => {
        setLoading(true);
        try {
            const {
                data: { session },
            } = await supabase.auth.getSession();

            if (!session) {
                notify("Not authenticated", { type: "error" });
                return;
            }

            const response = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/system-create-tenant`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to fetch organizations");
            }

            setOrganizations(data.data || []);
        } catch (error: any) {
            console.error("Error fetching organizations:", error);
            notify(error.message || "Failed to fetch organizations", {
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]);

    const handleInputChange = (field: keyof CreateOrgFormData, value: string) => {
        setFormData((prev) => {
            const updated = { ...prev, [field]: value };

            // Auto-generate slug from org name
            if (field === "orgName") {
                updated.orgSlug = value
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-|-$/g, "")
                    .substring(0, 50);
            }

            return updated;
        });
    };

    const handleCreateOrg = async () => {
        // Validation
        if (!formData.orgName.trim()) {
            notify("Organization name is required", { type: "warning" });
            return;
        }
        if (!formData.orgSlug.trim()) {
            notify("Organization slug is required", { type: "warning" });
            return;
        }
        if (!formData.ownerEmail.trim()) {
            notify("Owner email is required", { type: "warning" });
            return;
        }

        setIsSubmitting(true);

        try {
            const {
                data: { session },
            } = await supabase.auth.getSession();

            if (!session) {
                notify("Not authenticated", { type: "error" });
                return;
            }

            const response = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/system-create-tenant`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        orgName: formData.orgName,
                        orgSlug: formData.orgSlug,
                        ownerEmail: formData.ownerEmail,
                        ownerFirstName: formData.ownerFirstName || undefined,
                        ownerLastName: formData.ownerLastName || undefined,
                        billingPlan: formData.billingPlan,
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to create organization");
            }

            notify(
                `Organization "${data.organization.name}" created successfully!${data.owner.isNewUser ? " Owner will receive an invitation email." : ""}`,
                { type: "success" }
            );

            // Reset form and close dialog
            setFormData(initialFormData);
            setIsDialogOpen(false);

            // Refresh the list
            fetchOrganizations();
        } catch (error: any) {
            console.error("Error creating organization:", error);
            notify(error.message || "Failed to create organization", {
                type: "error",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                        <Building2 className="h-8 w-8" />
                        System Administration
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Manage organizations and tenants across the platform
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchOrganizations}
                        disabled={loading}
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className="h-4 w-4 mr-2" />
                                Create Organization
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[500px]">
                            <DialogHeader>
                                <DialogTitle>Create New Organization</DialogTitle>
                                <DialogDescription>
                                    Create a new tenant organization and assign an owner. The owner
                                    will be invited to set their password if they don't have an
                                    account.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                                {/* Organization Details */}
                                <div className="space-y-2">
                                    <Label htmlFor="orgName">Organization Name *</Label>
                                    <Input
                                        id="orgName"
                                        placeholder="Bob's Roofing"
                                        value={formData.orgName}
                                        onChange={(e) => handleInputChange("orgName", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="orgSlug">
                                        Slug *{" "}
                                        <span className="text-muted-foreground text-xs">
                                            (URL-friendly identifier)
                                        </span>
                                    </Label>
                                    <Input
                                        id="orgSlug"
                                        placeholder="bobs-roofing"
                                        value={formData.orgSlug}
                                        onChange={(e) =>
                                            handleInputChange(
                                                "orgSlug",
                                                e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")
                                            )
                                        }
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Will be accessible at: {formData.orgSlug || "slug"}.crm.salesfu.ai
                                    </p>
                                </div>

                                {/* Owner Details */}
                                <div className="border-t pt-4 mt-2">
                                    <h4 className="font-medium mb-3">Owner Details</h4>
                                    <div className="space-y-3">
                                        <div className="space-y-2">
                                            <Label htmlFor="ownerEmail">Owner Email *</Label>
                                            <Input
                                                id="ownerEmail"
                                                type="email"
                                                placeholder="owner@company.com"
                                                value={formData.ownerEmail}
                                                onChange={(e) =>
                                                    handleInputChange("ownerEmail", e.target.value)
                                                }
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-2">
                                                <Label htmlFor="ownerFirstName">First Name</Label>
                                                <Input
                                                    id="ownerFirstName"
                                                    placeholder="Bob"
                                                    value={formData.ownerFirstName}
                                                    onChange={(e) =>
                                                        handleInputChange("ownerFirstName", e.target.value)
                                                    }
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="ownerLastName">Last Name</Label>
                                                <Input
                                                    id="ownerLastName"
                                                    placeholder="Smith"
                                                    value={formData.ownerLastName}
                                                    onChange={(e) =>
                                                        handleInputChange("ownerLastName", e.target.value)
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Billing */}
                                <div className="border-t pt-4 mt-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="billingPlan">Billing Plan</Label>
                                        <Select
                                            value={formData.billingPlan}
                                            onValueChange={(value) =>
                                                handleInputChange("billingPlan", value)
                                            }
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a plan" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="starter">Starter</SelectItem>
                                                <SelectItem value="professional">Professional</SelectItem>
                                                <SelectItem value="enterprise">Enterprise</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => setIsDialogOpen(false)}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button onClick={handleCreateOrg} disabled={isSubmitting}>
                                    {isSubmitting ? "Creating..." : "Create Organization"}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Organizations List */}
            <Card>
                <CardHeader>
                    <CardTitle>Organizations</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="space-y-3">
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-12 w-full" />
                        </div>
                    ) : organizations.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <Building2 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>No organizations found</p>
                            <p className="text-sm">Create your first organization above</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Organization</TableHead>
                                    <TableHead>Slug</TableHead>
                                    <TableHead>Owner</TableHead>
                                    <TableHead>Plan</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Created</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {organizations.map((org) => (
                                    <TableRow key={org.id}>
                                        <TableCell className="font-medium">{org.name}</TableCell>
                                        <TableCell>
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded">
                                                {org.slug}
                                            </code>
                                        </TableCell>
                                        <TableCell>
                                            {org.owner ? (
                                                <div>
                                                    <div className="font-medium">
                                                        {org.owner.firstName} {org.owner.lastName}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {org.owner.email}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">No owner</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="capitalize">
                                                {org.billingPlan}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {org.isActive ? (
                                                <Badge variant="default" className="bg-green-600">
                                                    <CheckCircle className="h-3 w-3 mr-1" />
                                                    Active
                                                </Badge>
                                            ) : (
                                                <Badge variant="destructive">
                                                    <XCircle className="h-3 w-3 mr-1" />
                                                    Inactive
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {formatDate(org.createdAt)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

SuperAdminPage.path = "/super-admin";
